import { supabase } from '@/lib/supabase';
import { Lang } from '@/data/constants';
import { UserType } from '@/data/constants';

/**
 * ACCOUNT STATE HELPERS — email verification + account-type repair.
 *
 * ONE source of truth for both fixes shipped on 2026-09-22:
 *   1. "I never got a confirmation email"  -> resendConfirmation()
 *   2. "I signed up as a contractor and got a homeowner dashboard"
 *      -> roleRepairStatus() / repairRole()
 *
 * Everything here talks to SERVER functions that re-derive the caller from the
 * JWT (account-verification, account-role-repair). Nothing here grants itself
 * anything; the browser only asks and renders the answer.
 */

/** Seconds a user must wait between confirmation-email sends. */
export const RESEND_COOLDOWN_SECONDS = 60;

export type VerificationState = 'unverified' | 'legacy_unverified' | 'verified' | string;

export interface VerificationStatus {
  ok: boolean;
  email?: string;
  state: VerificationState;
  verified: boolean;
  /** Accounts created before confirmation was enforced. Never newly issued. */
  legacy: boolean;
  phoneVerified: boolean;
  accountActive: boolean;
}

export const EMPTY_VERIFICATION: VerificationStatus = {
  ok: false,
  state: 'unverified',
  verified: false,
  legacy: false,
  phoneVerified: false,
  accountActive: true,
};

const asStatus = (d: Record<string, unknown> | null | undefined): VerificationStatus => {
  const state = String(d?.email_verification_state || 'unverified');
  return {
    ok: Boolean(d?.ok),
    email: (d?.email as string) || undefined,
    state,
    verified: state === 'verified' || d?.email_verified === true,
    legacy: state === 'legacy_unverified',
    phoneVerified: d?.phone_verified === true,
    accountActive: d?.account_active !== false,
  };
};

/**
 * Current verification state for the signed-in account.
 *
 * `sync` first asks the server to promote the account when the auth provider
 * has ALREADY confirmed the mailbox after the enforcement cutoff — that
 * promotion is server-side and based on the provider's own timestamp, never on
 * anything the browser claims.
 */
export const fetchVerification = async (sync = true): Promise<VerificationStatus> => {
  try {
    if (sync) {
      await supabase.functions.invoke('account-verification', { body: { action: 'sync_email' } });
    }
    const { data, error } = await supabase.functions.invoke('account-verification', {
      body: { action: 'status' },
    });
    if (error || !data) return EMPTY_VERIFICATION;
    return asStatus(data as Record<string, unknown>);
  } catch {
    return EMPTY_VERIFICATION;
  }
};

export interface ResendResult {
  ok: boolean;
  /** 'provider' = auth confirmation mail, 'account' = our own account email. */
  via?: 'provider' | 'account';
  /** Set when the mailbox was already confirmed, so there is nothing to confirm. */
  alreadyConfirmed?: boolean;
  rateLimited?: boolean;
  retryAfter?: number;
  error?: string;
}

/**
 * Send (or re-send) the GIGGONE email-ownership link for the SIGNED-IN account.
 *
 * REAL VERIFICATION (2026-09-24). The auth provider auto-confirms every
 * address, which proves nothing about mailbox ownership. account-verification
 * now issues a signed ONE-TIME link (24 h) and mails it through
 * send-transactional-email; the verify-email function validates it and is the
 * only writer of email_verification_state = 'verified'.
 *
 * Server-side limits: 5 / hour / user and 10 / hour / address.
 * `lang`, `userId` and `name` are kept for call-site compatibility — the
 * server reads the language and name from the profile row itself.
 */
export const sendVerificationLink = async (source = 'resend'): Promise<ResendResult> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-verification', {
      body: { action: 'send_verification', source },
    });
    let payload = (data || {}) as Record<string, unknown>;
    // Non-2xx (429 rate limit, 502 send failure) arrives as an error whose
    // body is still the JSON verdict — read it so the user sees the real cause.
    if (!data && error && (error as any).context?.json) {
      try {
        payload = (await (error as any).context.json()) || {};
      } catch {
        /* keep empty payload */
      }
    }
    if (payload.ok && payload.already_verified) return { ok: true, via: 'account', alreadyConfirmed: true };
    if (payload.ok) return { ok: true, via: 'account' };
    if (payload.error === 'rate_limited') {
      return { ok: false, rateLimited: true, retryAfter: Number(payload.retry_after || 0) };
    }
    return { ok: false, error: String(payload.error || 'send_failed') };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};

export const resendConfirmation = async (
  _email: string,
  _lang: Lang,
  _userId?: string,
  _name?: string,
): Promise<ResendResult> => sendVerificationLink('resend');

/**
 * Does this sign-in failure mean "the address exists but is not confirmed"?
 *
 * The auth provider answers unconfirmed sign-ins with a distinct message, and
 * we must not flatten it into "email or password is incorrect" — that is what
 * left people with no way to recover on their own.
 */
export const isUnconfirmedError = (message?: string | null): boolean => {
  const m = (message || '').toLowerCase();
  return (
    m.includes('email not confirmed') ||
    m.includes('email_not_confirmed') ||
    m.includes('not confirmed') ||
    m.includes('confirm your email') ||
    m.includes('correo no confirmado')
  );
};

/* ------------------------------------------------------------------ */
/* ACCOUNT TYPE REPAIR                                                 */
/* ------------------------------------------------------------------ */

export interface RoleRepairStatus {
  looksMisroled: boolean;
  repairAvailable: boolean;
  alreadyRepaired: boolean;
  evidence: string[];
  userType?: UserType;
}

export const EMPTY_ROLE_REPAIR: RoleRepairStatus = {
  looksMisroled: false,
  repairAvailable: false,
  alreadyRepaired: false,
  evidence: [],
};

/**
 * Detects the signup race that stored a company registration as a project
 * owner (company_name / contact_name / trades present, user_type 'homeowner').
 * The server decides — the browser never guesses.
 */
export const roleRepairStatus = async (): Promise<RoleRepairStatus> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-role-repair', {
      body: { action: 'status' },
    });
    if (error || !data?.ok) return EMPTY_ROLE_REPAIR;
    return {
      looksMisroled: data.looks_misroled === true,
      repairAvailable: data.repair_available === true,
      alreadyRepaired: data.already_repaired === true,
      evidence: Array.isArray(data.evidence) ? data.evidence : [],
      userType: data.user_type as UserType,
    };
  } catch {
    return EMPTY_ROLE_REPAIR;
  }
};

export const repairRole = async (
  userType: Extract<UserType, 'gc' | 'subcontractor'>,
): Promise<{ ok: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('account-role-repair', {
      body: { action: 'repair', user_type: userType },
    });
    if (error) return { ok: false, error: error.message };
    if (data?.ok) return { ok: true };
    return { ok: false, error: String(data?.error || 'repair_failed') };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};
